import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Plane,
  CalendarDays,
  Shield,
  Check,
  Menu,
  X,
  ArrowRight,
  Star,
  Users,
  Clock,
  BookOpen,
  StickyNote,
  TrendingUp,
  ShieldCheck,
  Globe,
  Zap,
  MapPin,
  FileText,
  Award,
  Briefcase,
  Pencil,
  Lock as LockIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';

export function LandingPage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const features = [
    {
      icon: Plane,
      title: 'eFlight 排班同步',
      description:
        '每日凌晨自动从航司 eFlight 系统同步航班排班数据，支持多航段任务链完整展示',
      color: 'bg-blue-50 text-blue-600',
    },
    {
      icon: BookOpen,
      title: 'FSOP 飞行记录',
      description:
        '同步 FSOP 飞行人员资质系统的飞行记录，支持明细查看与自定义视图配置',
      color: 'bg-emerald-50 text-emerald-600',
    },
    {
      icon: ShieldCheck,
      title: '起落有效期管理',
      description:
        '基于最近 90 天飞行记录自动计算起落合规状态，PF 飞行与模拟机训练混合计算',
      color: 'bg-orange-50 text-orange-600',
    },
    {
      icon: Globe,
      title: '航线地图可视化',
      description:
        '3D 地球可视化展示飞行航线网络，支持机场坐标编辑与航线动态渲染',
      color: 'bg-indigo-50 text-indigo-600',
    },
    {
      icon: CalendarDays,
      title: 'iOS 日历订阅',
      description:
        '生成专属 ICS 订阅链接，一键同步到 iPhone 原生日历，排班变动实时更新',
      color: 'bg-purple-50 text-purple-600',
    },
    {
      icon: Pencil,
      title: '智能笔记系统',
      description:
        '在全部笔记中按航线、机场、机组成员多维度筛选与分组浏览，快速检索',
      color: 'bg-rose-50 text-rose-600',
    },
    {
      icon: TrendingUp,
      title: '职业生涯管理',
      description:
        '记录职业阶段变迁，支持阶段类型、机型、单位变更，汇总统计与历史追溯',
      color: 'bg-cyan-50 text-cyan-600',
    },
    {
      icon: Zap,
      title: '自动同步调度',
      description:
        'Celery 定时任务自动执行数据同步，支持手动触发与同步进度实时追踪',
      color: 'bg-amber-50 text-amber-600',
    },
  ];

  const modules = [
    {
      icon: FileText,
      title: '排班管理',
      items: ['航班链与航段明细', '日历视图', '每日事件提醒', '排班变动通知'],
    },
    {
      icon: Award,
      title: '飞行资质',
      items: ['飞行记录同步', '起落次数统计', '90 天合规检查', '视图自定义配置'],
    },
    {
      icon: StickyNote,
      title: '笔记中心',
      items: ['航线笔记', '机场笔记', '机组笔记', '富文本编辑'],
    },
    {
      icon: Briefcase,
      title: '职业档案',
      items: ['阶段记录', '机型变更', '单位调动', '履历统计'],
    },
  ];

  const steps = [
    {
      step: '01',
      title: '注册账号',
      description: '创建 FlightSync Pro 账号，配置 eFlight 与 FSOP 登录信息',
    },
    {
      step: '02',
      title: '同步数据',
      description: '系统自动同步航班排班与飞行记录，支持手动触发即时更新',
    },
    {
      step: '03',
      title: '订阅日历',
      description: '复制 ICS 链接到 iPhone 日历，排班变动自动推送',
    },
    {
      step: '04',
      title: '管理记录',
      description: '查看飞行记录、笔记、职业阶段，全面掌握个人飞行生涯',
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-xl bg-gradient-primary flex items-center justify-center">
                <Plane className="w-6 h-6 text-white" />
              </div>
              <span className="text-xl font-bold text-gray-900">FlightSync</span>
              <span className="text-xs font-medium text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full">
                Pro
              </span>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center gap-8">
              <a
                href="#features"
                className="text-gray-600 hover:text-gray-900 transition-colors"
              >
                功能
              </a>
              <a
                href="#modules"
                className="text-gray-600 hover:text-gray-900 transition-colors"
              >
                模块
              </a>
              <a
                href="#how-it-works"
                className="text-gray-600 hover:text-gray-900 transition-colors"
              >
                使用流程
              </a>
            </div>

            {/* CTA Buttons */}
            <div className="hidden md:flex items-center gap-4">
              <Link
                to="/login"
                className="text-gray-600 hover:text-gray-900 transition-colors"
              >
                登录
              </Link>
              <Link to="/register">
                <Button>免费注册</Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6" />
              ) : (
                <Menu className="w-6 h-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-white border-t border-gray-100">
            <div className="px-4 py-4 space-y-3">
              <a href="#features" className="block py-2 text-gray-600">
                功能
              </a>
              <a href="#modules" className="block py-2 text-gray-600">
                模块
              </a>
              <a href="#how-it-works" className="block py-2 text-gray-600">
                使用流程
              </a>
              <hr />
              <Link to="/login" className="block py-2 text-gray-600">
                登录
              </Link>
              <Link to="/register" className="block py-2">
                <Button className="w-full">免费注册</Button>
              </Link>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-full text-blue-600 text-sm font-medium mb-6">
                <Star className="w-4 h-4" />
                专为航空机组人员打造
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
                一站式航班数据
                <span className="text-blue-600">同步与管理平台</span>
              </h1>
              <p className="text-lg text-gray-600 mb-8 max-w-lg">
                自动同步 eFlight 排班与 FSOP 飞行记录，生成 iPhone
                日历订阅，管理笔记与职业生涯，让机组人员高效掌控每一次飞行。
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/register">
                  <Button size="lg" className="gap-2">
                    立即开始使用
                    <ArrowRight className="w-4 h-4" />
                  </Button>
                </Link>
                <a href="#features">
                  <Button size="lg" variant="outline">
                    了解更多
                  </Button>
                </a>
              </div>
              <div className="flex items-center gap-6 mt-8 text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  免费使用
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  无需安装 App
                </div>
                <div className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-green-500" />
                  多端访问
                </div>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-600 rounded-3xl blur-3xl opacity-20"></div>
              <div className="relative bg-white rounded-2xl shadow-2xl p-6 border border-gray-100">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                      <Plane className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium">今日航班</p>
                      <p className="text-sm text-gray-500">2026年4月27日</p>
                    </div>
                  </div>
                  <span className="text-2xl font-bold text-blue-600">3</span>
                </div>
                <div className="space-y-3">
                  {[
                    { flight: 'MU5101', route: 'SHA-PEK', time: '08:00', status: '已签到' },
                    { flight: 'MU5102', route: 'PEK-SHA', time: '14:30', status: '准备中' },
                    { flight: 'MU5103', route: 'SHA-CAN', time: '19:00', status: '计划' },
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <span className="font-semibold text-blue-600">
                          {item.flight}
                        </span>
                        <span className="text-gray-500">{item.route}</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <span className="text-xs px-2 py-1 bg-white rounded-full text-gray-500 border">
                          {item.status}
                        </span>
                        <span className="text-gray-900 font-medium">
                          {item.time}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: '1000+', label: '活跃用户', icon: Users },
              { value: '50000+', label: '同步航班', icon: Plane },
              { value: '99.9%', label: '可用性', icon: Check },
              { value: '24/7', label: '自动同步', icon: Clock },
            ].map((stat, i) => (
              <div key={i} className="text-center">
                <stat.icon className="w-8 h-8 text-blue-600 mx-auto mb-3" />
                <p className="text-3xl font-bold text-gray-900">{stat.value}</p>
                <p className="text-gray-500 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              核心功能
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              覆盖航班排班、飞行记录、资质管理、笔记与职业生涯的全方位解决方案
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, i) => (
              <div
                key={i}
                className="p-6 bg-white rounded-2xl border border-gray-100 hover:shadow-lg transition-shadow"
              >
                <div
                  className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 ${feature.color}`}
                >
                  <feature.icon className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Modules Section */}
      <section id="modules" className="py-20 bg-gray-50 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              四大核心模块
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              围绕机组人员日常工作场景，打造专业、高效的数据管理体验
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {modules.map((module, i) => (
              <div
                key={i}
                className="bg-white rounded-2xl p-8 border border-gray-100 hover:shadow-lg transition-shadow"
              >
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                    <module.icon className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">
                    {module.title}
                  </h3>
                </div>
                <ul className="space-y-3">
                  {module.items.map((item, j) => (
                    <li key={j} className="flex items-center gap-3 text-gray-600">
                      <Check className="w-4 h-4 text-green-500 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section
        id="how-it-works"
        className="py-20 px-4 sm:px-6 lg:px-8"
      >
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              使用流程
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              四步即可开始使用，轻松管理您的航班日程与飞行生涯
            </p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {steps.map((item, i) => (
              <div key={i} className="relative">
                <div className="text-6xl font-bold text-blue-100 mb-4">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">
                  {item.title}
                </h3>
                <p className="text-gray-600">{item.description}</p>
                {i < steps.length - 1 && (
                  <div className="hidden md:block absolute top-8 right-0 transform translate-x-1/2">
                    <ArrowRight className="w-8 h-8 text-gray-300" />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Security & Trust */}
      <section className="py-20 bg-gray-50 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-6">
                安全可靠的数据保护
              </h2>
              <p className="text-gray-600 mb-8">
                我们深知飞行数据的安全重要性，采用多层加密与权限控制，确保您的个人信息与飞行记录安全无虞。
              </p>
              <div className="space-y-4">
                {[
                  {
                    icon: Shield,
                    title: 'AES-256 加密存储',
                    desc: 'eFlight 与 FSOP 密码使用 Fernet (AES-256-GCM) 加密，密钥哈希后存储',
                  },
                  {
                    icon: LockIcon,
                    title: 'JWT 身份认证',
                    desc: 'Access Token 24 小时 / Refresh Token 7 天，启用令牌轮换机制',
                  },
                  {
                    icon: MapPin,
                    title: 'ICS Token 保护',
                    desc: '日历订阅链接通过不可猜测的随机 Token 保护，公开访问无需暴露账号',
                  },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-blue-600" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">
                        {item.title}
                      </h4>
                      <p className="text-gray-600 text-sm">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-blue-500 rounded-3xl blur-3xl opacity-10"></div>
              <div className="relative bg-white rounded-2xl shadow-xl p-8 border border-gray-100">
                <div className="flex items-center gap-3 mb-6">
                  <Shield className="w-8 h-8 text-emerald-500" />
                  <div>
                    <p className="font-semibold text-gray-900">安全合规</p>
                    <p className="text-sm text-gray-500">数据加密与访问控制</p>
                  </div>
                </div>
                <div className="space-y-4">
                  {[
                    { label: '密码加密', status: 'AES-256-GCM', ok: true },
                    { label: '传输加密', status: 'HTTPS/TLS', ok: true },
                    { label: '敏感字段', status: 'write_only', ok: true },
                    { label: '权限控制', status: 'IsAuthenticated', ok: true },
                  ].map((item, i) => (
                    <div
                      key={i}
                      className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                    >
                      <span className="text-gray-700">{item.label}</span>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-500">
                          {item.status}
                        </span>
                        <Check className="w-4 h-4 text-green-500" />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-gradient-primary rounded-3xl p-12 text-white">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              准备好开始了吗？
            </h2>
            <p className="text-lg text-blue-100 mb-8 max-w-xl mx-auto">
              立即注册 FlightSync Pro，让航班排班同步、飞行记录管理与日程安排变得简单高效
            </p>
            <Link to="/register">
              <Button size="lg" variant="secondary" className="gap-2">
                免费注册使用
                <ArrowRight className="w-4 h-4" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-gray-100 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-primary flex items-center justify-center">
                <Plane className="w-4 h-4 text-white" />
              </div>
              <span className="font-bold text-gray-900">FlightSync Pro</span>
            </div>
            <p className="text-gray-500 text-sm">
              专为航空公司机组人员打造的全栈数据同步平台
            </p>
            <div className="flex items-center gap-6">
              <Link
                to="/login"
                className="text-gray-600 hover:text-gray-900 text-sm"
              >
                登录
              </Link>
              <Link
                to="/register"
                className="text-gray-600 hover:text-gray-900 text-sm"
              >
                注册
              </Link>
            </div>
          </div>
          <div className="mt-4 pt-4 border-t border-gray-100 text-center">
            <a
              href="https://beian.miit.gov.cn/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-gray-600 text-xs transition-colors"
            >
              渝ICP备2026015661号
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
